import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";

type Hsv = { h: number; s: number; v: number };

const SLOT_COUNT = 3;
const MAX_SLOT_COUNT = 6;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const hexToRgb = (hex: string): [number, number, number] | null => {
  const value = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(value)) {
    return null;
  }
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b]
    .map(channel =>
      Math.round(clamp(channel, 0, 255))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")
    .toUpperCase()}`;

const rgbToHsv = (r: number, g: number, b: number): Hsv => {
  const nr = r / 255;
  const ng = g / 255;
  const nb = b / 255;
  const max = Math.max(nr, ng, nb);
  const min = Math.min(nr, ng, nb);
  const delta = max - min;
  let h = 0;
  if (delta !== 0) {
    if (max === nr) {
      h = ((ng - nb) / delta + (ng < nb ? 6 : 0)) / 6;
    } else if (max === ng) {
      h = ((nb - nr) / delta + 2) / 6;
    } else {
      h = ((nr - ng) / delta + 4) / 6;
    }
  }
  return { h: h * 360, s: max === 0 ? 0 : delta / max, v: max };
};

const hsvToRgb = (hsv: Hsv): [number, number, number] => {
  const h = ((hsv.h % 360) + 360) % 360;
  const c = hsv.v * hsv.s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = hsv.v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
};

const hexToHsv = (hex: string): Hsv => {
  const rgb = hexToRgb(hex) || [196, 181, 165];
  return rgbToHsv(rgb[0], rgb[1], rgb[2]);
};

const hsvToHex = (hsv: Hsv) => {
  const [r, g, b] = hsvToRgb(hsv);
  return rgbToHex(r, g, b);
};

const hueColor = (h: number) => hsvToHex({ h, s: 1, v: 1 });

type Props = {
  slots: Array<string | null>;
  selectedSlot: number;
  onSelectSlot: (index: number) => void;
  onChangeSlot: (index: number, hex: string) => void;
  onAddCurrent: (hex: string) => void;
  onAddSlot: () => void;
  onRemoveSlot: (index: number) => void;
  onToggle?: () => void;
};

const ImageGeneratorMakeOwnPicker: React.FC<Props> = ({
  slots,
  selectedSlot,
  onSelectSlot,
  onChangeSlot,
  onAddCurrent,
  onAddSlot,
  onRemoveSlot,
  onToggle,
}) => {
  const selectedHex = slots[selectedSlot] || "#C4B5A5";
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(selectedHex));
  const hsvRef = useRef(hsv);
  const planeRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const dragging = useRef<"plane" | "hue" | null>(null);
  const slotsRef = useRef(slots);
  const selectedSlotRef = useRef(selectedSlot);
  const onChangeSlotRef = useRef(onChangeSlot);

  useEffect(() => {
    hsvRef.current = hsv;
  }, [hsv]);

  useEffect(() => {
    slotsRef.current = slots;
    selectedSlotRef.current = selectedSlot;
    onChangeSlotRef.current = onChangeSlot;
  }, [slots, selectedSlot, onChangeSlot]);

  useEffect(() => {
    setHsv(hexToHsv(selectedHex));
  }, [selectedHex]);

  const commitHsv = useCallback((next: Hsv) => {
    const clamped = {
      h: clamp(next.h, 0, 360),
      s: clamp(next.s, 0, 1),
      v: clamp(next.v, 0, 1),
    };
    hsvRef.current = clamped;
    setHsv(clamped);
    const hex = hsvToHex(clamped);
    const index = selectedSlotRef.current;
    if (slotsRef.current[index]) {
      onChangeSlotRef.current(index, hex);
    }
  }, []);

  const readPlane = useCallback(
    (event: PointerEvent | React.PointerEvent) => {
      const node = planeRef.current;
      if (!node) {
        return;
      }
      const box = node.getBoundingClientRect();
      const s = clamp((event.clientX - box.left) / box.width, 0, 1);
      const v = clamp(1 - (event.clientY - box.top) / box.height, 0, 1);
      commitHsv({ ...hsvRef.current, s, v });
    },
    [commitHsv]
  );

  const readHue = useCallback(
    (event: PointerEvent | React.PointerEvent) => {
      const node = hueRef.current;
      if (!node) {
        return;
      }
      const box = node.getBoundingClientRect();
      const h = clamp(((event.clientX - box.left) / box.width) * 360, 0, 360);
      commitHsv({ ...hsvRef.current, h });
    },
    [commitHsv]
  );

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (dragging.current === "plane") {
        readPlane(event);
      } else if (dragging.current === "hue") {
        readHue(event);
      }
    };
    const onUp = () => {
      dragging.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [readHue, readPlane]);

  const currentHex = hsvToHex(hsv);

  return (
    <div className="w-[200px] overflow-hidden rounded-2xl border border-[#737373] bg-white shadow-lg">
      <button
        type="button"
        onClick={onToggle}
        className="flex h-8 w-full items-center gap-2 border-b border-[#E2E2E2] px-3 py-1 text-left"
      >
        <span
          className="h-5 w-5 shrink-0 rounded-full border border-[#737373]"
          style={{ backgroundColor: currentHex }}
        />
        <span className="flex-1 truncate text-xs leading-none text-secondary">
          + Make your own
        </span>
        <DropdownArrow className="h-2 w-2 rotate-180" />
      </button>

      <div className="p-3 pt-2">
        <div
          ref={planeRef}
          className="relative h-[120px] w-full cursor-crosshair overflow-hidden rounded-lg border border-[#737373]/40"
          style={{
            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor(
              hsv.h
            )})`,
          }}
          onPointerDown={event => {
            dragging.current = "plane";
            readPlane(event);
          }}
          onDoubleClick={() => onAddCurrent(currentHex)}
        >
          <span
            className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
          >
            <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-white shadow" />
            <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-white shadow" />
          </span>
        </div>

        <div
          ref={hueRef}
          className="relative mt-2 h-3 w-full cursor-pointer overflow-hidden rounded-full border border-[#737373]/30"
          style={{
            background:
              "linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)",
          }}
          onPointerDown={event => {
            dragging.current = "hue";
            readHue(event);
          }}
        >
          <span
            className="pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
            style={{
              left: `${(hsv.h / 360) * 100}%`,
              backgroundColor: hueColor(hsv.h),
            }}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
          {slots.map((hex, index) => {
            const isSelected = selectedSlot === index;
            const filled = Boolean(hex);
            return (
              <div key={index} className="relative">
                <button
                  type="button"
                  aria-label={
                    filled
                      ? `Edit colour ${hex}`
                      : `Set colour slot ${index + 1}`
                  }
                  onClick={() => {
                    onSelectSlot(index);
                    if (!filled) {
                      onAddCurrent(currentHex);
                    }
                  }}
                  className={`h-7 w-7 rounded-full border outline-none focus-visible:outline-none ${
                    isSelected
                      ? "border-[#737373] ring-2 ring-[#737373]/30"
                      : "border-[#737373]/50"
                  }`}
                  style={{ backgroundColor: hex || "#D9D9D9" }}
                />
                {filled ? (
                  <button
                    type="button"
                    aria-label={`Remove colour ${hex}`}
                    title="Remove colour"
                    onClick={event => {
                      event.stopPropagation();
                      onRemoveSlot(index);
                    }}
                    className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-[#737373] bg-white text-[11px] leading-none text-secondary shadow-sm hover:bg-[#F5F5F5]"
                  >
                    −
                  </button>
                ) : null}
              </div>
            );
          })}
          {slots.length < MAX_SLOT_COUNT ? (
            <button
              type="button"
              aria-label="Add another colour slot"
              onClick={onAddSlot}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-[#737373] text-sm leading-none text-secondary outline-none focus-visible:outline-none"
            >
              +
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const MAKE_OWN_DEFAULT_SLOTS = SLOT_COUNT;
export const MAKE_OWN_MAX_SLOTS = MAX_SLOT_COUNT;

export const emptyMakeOwnSlots = (
  hexes: string[] = []
): Array<string | null> => {
  const count = Math.min(
    MAKE_OWN_MAX_SLOTS,
    Math.max(MAKE_OWN_DEFAULT_SLOTS, hexes.length)
  );
  const next: Array<string | null> = Array.from({ length: count }, () => null);
  hexes.slice(0, MAKE_OWN_MAX_SLOTS).forEach((hex, index) => {
    next[index] = hex;
  });
  return next;
};

export default ImageGeneratorMakeOwnPicker;
